import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { AlertButtonColor } from "@obg/mat.ui.core";
import { CommonState } from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	Jurisdiction,
	JurisdictionSelector,
	ProductType,
	ResponsibleGamingAction,
	ResponsibleGamingConfigService,
	Timeout,
	TimeoutMinutes,
} from "@obg/ngx.core";
import {
	CoreContentState,
	ImageSelector,
	TranslationGroup,
	TranslationSelector,
	TranslationService,
} from "@obg/ngx.core.content";
import { ignoreNil } from "@obg/rxjs";
import { Observable, of } from "rxjs";
import { filter, map, take, tap } from "rxjs/operators";

import { LpcseTimeOffConfirmContainer } from "./lpcse/lpcse-time-off-confirm.container";
import { Prefix, TimeOffConfig, TimeOffContent, TimeoutTranslation, TIME_OFF_ALERT_ID } from "./time-off.model";

@Injectable({ providedIn: "root" })
export class TimeOffService {
	private currentJurisdiction$: Observable<Jurisdiction>;

	constructor(
		private translationService: TranslationService,
		private imageSelector: ImageSelector,
		private store: Store<CommonState & CoreContentState>,
		private responsibleGamingConfig: ResponsibleGamingConfigService,
		private alertService: AbstractTranslatableAlertService,
		private responsibleGamingAction: ResponsibleGamingAction,
		private translationSelector: TranslationSelector
	) {
		this.currentJurisdiction$ = this.store.select(JurisdictionSelector.get).pipe(ignoreNil(), take(1));
	}

	getTimeOffPeriods$(): Observable<string[]> {
		const timeOffPeriods = this.responsibleGamingConfig
			.getCoolOffTimeouts()
			.map((period) => period.text.split(".")[1]);
		return of(timeOffPeriods);
	}

	// TODO: this will be removed once CRS service is sorted for MFE
	getTimeOffTranslations$(): Observable<TimeoutTranslation[]> | undefined {
		const timeOffPeriods = this.responsibleGamingConfig.getCoolOffTimeouts();
		return this.store.pipe(
			this.translationSelector.getByKeys$(
				timeOffPeriods.map((res) => res.text),
				["common"]
			),
			map((translations) =>
				timeOffPeriods.map((timeout, index) => ({
					key: timeout.text.split(".")[1],
					translation: translations[index],
				}))
			)
		);
	}

	getTimOffContent$(): Observable<TimeOffContent> {
		return this.store
			.select(this.imageSelector.getByKey(ProductType.Common, `responsible-gaming.${Prefix.SelfExclusion}`))
			.pipe(
				filter((image) => !!image),
				take(1),
				map((img) => ({
					coolOffCaption: this.translationService.getByKey(`${Prefix.SelfExclusion}.cooloff.caption`),
					coolOffCta: this.translationService.getByKey(`${Prefix.SelfExclusion}.cooloff.cta`),
					imageUrl: img!.url,
					timeOffCaption: this.translationService.getByKey(`${Prefix.TimeOff}.caption`),
					timeOffPeriod: this.translationService.getByKey(`${Prefix.TimeOff}.period`),
					timeoutCta: this.translationService.getByKey(`${Prefix.SelfExclusion}.timeout`),
					timeoutRegulation: this.translationService.getByKey(`${Prefix.TimeOff}.regulation`),
				}))
			);
	}

	getTimeOffComponentConfig$(): Observable<TimeOffConfig> {
		return this.currentJurisdiction$.pipe(
			map((jurisdiction) => ({
				isTimeOffHidden: false,
				isCoolOffEnabled: jurisdiction === Jurisdiction.Sga,
				showRegulationText: jurisdiction === Jurisdiction.Sga || jurisdiction === Jurisdiction.Dga,
			}))
		);
	}

	show24HourBreakAlert(handler?: (timeout: Timeout | TimeoutMinutes) => void) {
		const timeout = {
			numberOfDays: 1,
			text: `${Prefix.SelfExclusion}.24-hours`,
		};
		return this.showTimeOffAlertByJurisdiction(timeout, handler);
	}

	showTimeOffAlert(key: string, handler?: (timeout: Timeout | TimeoutMinutes) => void) {
		const timeout = this.getTimeoutByKey(key);
		return this.showTimeOffAlertByJurisdiction(timeout, handler);
	}

	dispatchTimeOutAction(timeout: Timeout | TimeoutMinutes) {
		if ("minutes" in timeout) {
			this.store.dispatch(
				this.responsibleGamingAction.updateInstantSelfExclusionPeriod({ minutes: timeout.minutes })
			);
			return;
		}

		this.store.dispatch(this.responsibleGamingAction.updateCoolOffPeriod({ days: timeout.numberOfDays }));
	}

	private getTimeoutByKey(key: string): Timeout | TimeoutMinutes {
		const timeout = this.responsibleGamingConfig.getCoolOffTimeouts().find((period) => period.text.includes(key));

		if (!timeout) {
			throw new Error("time-off.service.ts::getCoolOffTimeoutByKey:: time-off value is missing in config");
		}

		return { ...timeout };
	}

	private showTimeOffAlertByJurisdiction(
		timeout: Timeout | TimeoutMinutes,
		handler?: (timeout: Timeout | TimeoutMinutes) => void
	) {
		this.store
			.select(JurisdictionSelector.get)
			.pipe(
				ignoreNil(),
				take(1),
				tap((jurisdiction) => {
					if (jurisdiction === Jurisdiction.LPCSE) {
						this.showLpcseTimeOffAlert(timeout, handler);
						return;
					}
					this.showDefaultTimeOffAlert(timeout, handler);
				})
			)
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe();
	}

	private showDefaultTimeOffAlert(
		timeout: Timeout | TimeoutMinutes,
		handler?: (timeout: Timeout | TimeoutMinutes) => void
	) {
		this.alertService.open(
			{
				prefixJurisdiction: true,
				id: TIME_OFF_ALERT_ID,
				testId: TIME_OFF_ALERT_ID,
				title: "time-off.confirm-title",
				message: "time-off.confirm-message",
				hideClose: true,
				dialogConfig: {
					disableClose: true,
				},
				mustBeInteracted: true,
				buttons: [
					{
						text: "general.cancel",
						color: AlertButtonColor.Primary,
					},
					{
						text: "general.confirm",
						handler: () => {
							if (handler) {
								handler(timeout);
								return;
							}
							this.dispatchTimeOutAction(timeout);
						},
					},
				],
			},
			[TranslationGroup.Common],
			{
				period: this.translationService.getByKey(timeout.text),
			}
		);
	}

	private showLpcseTimeOffAlert(
		timeout: Timeout | TimeoutMinutes,
		handler?: (timeout: Timeout | TimeoutMinutes) => void
	) {
		this.alertService.open(
			{
				id: TIME_OFF_ALERT_ID,
				testId: TIME_OFF_ALERT_ID,
				messageComponent: LpcseTimeOffConfirmContainer,
				messageComponentInit: (component: LpcseTimeOffConfirmContainer) =>
					(component.data = {
						timeout: timeout,
						confirmHandler: (t: Timeout | TimeoutMinutes) => {
							if (handler) {
								handler(t);
								return;
							}
							this.dispatchTimeOutAction(t);
						},
					}),
				mustBeInteracted: true,
				hideClose: false,
				dialogConfig: {
					disableClose: false,
				},
			},
			[]
		);
	}
}
